/**
 * <TutorSoundModeSwitch> — Dialogue ou Concentré, d'un geste, sous la vidéo d'un élève (molecule).
 *
 * L'ancien bouton son de la tuile (`LiveParticipant`, haut-parleur vert ou barré violet) ne disait
 * l'état que par sa couleur. Ici, les deux modes côte à côte, **en icônes** (Wajih, round 4 : « mieux vaut
 * des icônes au lieu du texte pour rendre ça moins chargé ») — le haut-parleur pour Dialogue, le son coupé
 * pour Concentré, les glyphes de l'ancienne interface, que les tuteurs comprennent (daily du 04/11 : le micro
 * se lisait comme le micro de l'élève) ; le mot reste au lecteur d'écran. Le mode posé prend son
 * aplat — la tinte de progression pour Dialogue, le violet plein du live pour Concentré — et l'autre
 * reste lisible, au gris, prêt à être touché. Un toucher sur l'autre bascule l'élève ; un toucher sur le
 * mode posé ne fait rien.
 *
 * `pending` : la bascule part (l'ancien `setSoundModeV2`) — la paire ne répond plus, sans roue :
 * l'écran passe la valeur du geste tout de suite. `disabled` : l'élève est parti (hors-ligne).
 *
 * Contrôlé : le mode arrive en prop, le toucher repart. La paire est un groupe de choix (`radiogroup`) ;
 * chaque mode fait 26 px de haut et s'étend à 44 au toucher.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { CHIP_HEIGHT } from "../../atoms/Badge/Badge"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { slopTo } from "../../utils/touchTarget"
import { SOUND_MODE_LABELS } from "../SoundModeBadge"

export type TutorSoundModeSwitchProps = {
    mode: UISoundMode
    onChange: (mode: UISoundMode) => void
    /** L'élève est parti : la paire se lit, ne se touche pas. Défaut `false`. */
    disabled?: boolean
    /** La bascule part : la paire ne répond plus. Défaut `false`. */
    pending?: boolean
    /** Le prénom de l'élève, pour le lecteur d'écran (« Mode de Léa »). */
    studentName?: string
    testID?: string
}

const ORDER: UISoundMode[] = ["dialogue", "focused"]

/**
 * Un mode, posé : son glyphe (celui de l'ancien bouton son de la tuile, `LiveParticipant` : `volume-up` /
 * `volume-mute`), son aplat et son encre, dans la paire. Le bouton incrusté dans la vidéo
 * (`<TutorSoundModeToggle>`) y lit son glyphe ; son aplat est celui d'un poussoir (`MODE_FILL`, plein, à relief).
 */
export const SOUND_MODE_FACE: Record<UISoundMode, { icon: IconName; fill: SemanticColorKey; ink: SemanticColorKey }> = {
    dialogue: { icon: "volume-up", fill: "successLight", ink: "success-strong" },
    focused: { icon: "volume-mute", fill: "purple", ink: "white" },
}

const HIT_SLOP = { top: slopTo(CHIP_HEIGHT), bottom: slopTo(CHIP_HEIGHT) }

export function TutorSoundModeSwitch(props: TutorSoundModeSwitchProps) {
    const { mode, onChange, disabled = false, pending = false, studentName, testID } = props

    return (
        <View
            style={styles.root}
            accessibilityRole="radiogroup"
            accessibilityLabel={studentName != null ? `Mode de ${studentName}` : "Mode du son"}
            testID={testID}
        >
            {ORDER.map((option) => {
                const chosen = option === mode
                return (
                    <Pressable
                        key={option}
                        onPress={chosen ? undefined : () => onChange(option)}
                        disabled={disabled || pending}
                        accessibilityRole="radio"
                        accessibilityState={{ checked: chosen, disabled: disabled || pending }}
                        accessibilityLabel={SOUND_MODE_LABELS[option]}
                        // `aria-checked` : react-native-web 0.20 ignore `accessibilityState`.
                        aria-checked={chosen}
                        hitSlop={HIT_SLOP}
                        style={styles.segment(chosen ? SOUND_MODE_FACE[option].fill : null, disabled)}
                    >
                        <Icon
                            name={SOUND_MODE_FACE[option].icon}
                            size={14}
                            color={chosen ? SOUND_MODE_FACE[option].ink : "text-muted"}
                        />
                    </Pressable>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        flexDirection: "row" as const,
        alignSelf: "flex-start" as const,
        padding: 2,
        gap: 2,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors["surface-subtle"],
    },
    segment: (fill: SemanticColorKey | null, disabled: boolean) => ({
        height: CHIP_HEIGHT - 4,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        paddingHorizontal: theme.spacing["3"],
        borderRadius: theme.radius.full,
        backgroundColor: fill != null ? theme.colors[fill] : "transparent",
        opacity: disabled ? 0.5 : 1,
    }),
}))
